import { useEffect, useState } from 'react';

import { useSitePlans } from '@hotjar/webapp/hooks/useSitePlans';
import type { SiteWithPriceInfo } from '@hotjar/webapp/domains/revenue/site/types';
import type { Plan } from '@hotjar/webapp/domains/revenue/plan/types';
import type { PriceInfo } from '@hotjar/webapp/domains/revenue/pricing/types';
import type { OwnedAccount } from '@hotjar/account';
import { isPlanDowngradePeriod, mapSiteDtoToModel } from '@hotjar/site';
import { isScaleSelfServe } from '@hotjar/webapp/domains/revenue/plan';
import { useTranslationWithPrefix } from '@hotjar/translations';
import { isZuoraEnabled } from '@hotjar/webapp/domains/billing/features';

import { SiteDetailsContinuousCapture } from './SiteDetailsContinuousCapture';
import { SiteDetailsSnapshots } from './SiteDetailsSnapshots';
import { SiteDetailsCollectionRate } from './SiteDetailsCollectionRate';
import { SitePrice } from '../SitePrice/SitePrice';
import { StyledBox, DetailsContainer, SiteName, CostContainer } from './Sitebox.styles';

interface Props {
  account: OwnedAccount;
  site: SiteWithPriceInfo;
  onPlanChange: (
    organizationId: number,
    siteId: number,
    plan: Pick<Plan, 'identifier'>,
  ) => Promise<void>;
  onCancelDowngrade: (site: SiteWithPriceInfo) => void;
  hasDivider: boolean;
  updatedSitePriceInfo: Record<number, PriceInfo> | null;
}

export const SiteBox = ({
  account,
  site,
  onPlanChange,
  hasDivider,
  onCancelDowngrade,
  updatedSitePriceInfo,
}: Props): JSX.Element => {
  const { t } = useTranslationWithPrefix('billing:site-box');
  // Initialize changedSite to the initial site
  const [changedSite, setChangedSite] = useState<SiteWithPriceInfo>(site);

  const { plan } = useSitePlans(changedSite);
  const planDowngradePeriod = isPlanDowngradePeriod(mapSiteDtoToModel(site));
  const isUnlimited = !isScaleSelfServe(account) && plan.featureGroup === 'scale';

  const zuoraEnabled = isZuoraEnabled(account);

  const hasContinuousCapture = plan.ccDailyAllowance > 0;
  const { id } = site;

  // Check if this site has updated pricing info.
  // updatedSitePriceInfo is an object with price changes for all owned sites
  // with schema: [{ siteId: {...PriceInfo}}, ...]
  // we do this to handle some quirkiness with how react detects prop changes
  // and also to prevent mutating $scope.model.organizations since this causes issues with the
  // ordering
  const updatedPriceInfo = updatedSitePriceInfo?.[id];
  const [planId, setActivePlanId] = useState(plan.identifier);

  // When we change the plan, set the planId to the selected plan,
  // update changed site with new plan identifier and set showHighlight
  // to true, so that the new price is highlighted.
  const handlePlanChange = async (organizationId: number, siteId: number, selectedPlan: Plan) => {
    const isSamePlan = selectedPlan.identifier === site.plan_id;

    setActivePlanId(selectedPlan.identifier);
    await onPlanChange(organizationId, siteId, { identifier: selectedPlan.identifier });
    setChangedSite(
      isSamePlan
        ? site
        : {
            ...site,
            plan_id: selectedPlan.identifier,
          },
    );
  };

  useEffect(() => {
    // if we cancel the prospected plan changes i.e. updatedSitePriceInfo === null
    // reset the active plan id to its initial value so the plan dropdown
    // resets.

    if (!updatedSitePriceInfo) {
      setActivePlanId(site.plan_id);
      setChangedSite(site);
    }
  }, [site, updatedSitePriceInfo]);

  return (
    <StyledBox id={`site-box-${site.id}`} hasDivider={hasDivider}>
      <DetailsContainer>
        <SiteName>{site.name}</SiteName>

        {hasContinuousCapture ? (
          <SiteDetailsContinuousCapture
            account={account}
            site={changedSite}
            unlimitedAllowance={isUnlimited}
          >
            <SiteDetailsCollectionRate
              account={account}
              site={changedSite}
              onPlanChange={handlePlanChange}
              {...(!zuoraEnabled && { onCancelDowngrade })}
              planId={planId}
              unlimitedSampleRate={isUnlimited}
            />
          </SiteDetailsContinuousCapture>
        ) : (
          <SiteDetailsSnapshots account={account} site={changedSite}>
            <SiteDetailsCollectionRate
              account={account}
              site={changedSite}
              onPlanChange={handlePlanChange}
              {...(!zuoraEnabled && { onCancelDowngrade })}
              planId={planId}
              unlimitedSampleRate={isUnlimited}
            />
          </SiteDetailsSnapshots>
        )}
      </DetailsContainer>

      <CostContainer>
        {/* If we have updated price info for this site, show new prices
        else show the original price values */}
        <SitePrice
          billingCycle={account.billingCycle}
          planDowngradePeriod={planDowngradePeriod}
          total={
            (isUnlimited && t('custom-price')) || updatedPriceInfo?.total || site.priceInfo.total
          }
          subtotal={updatedPriceInfo?.subtotal || site.priceInfo.subtotal}
          showSubtotal={updatedPriceInfo?.showSubtotal || site.priceInfo.showSubtotal}
          legacyDiscount={updatedPriceInfo?.legacyDiscount || site.priceInfo.legacyDiscount}
          showAgencyDiscount={updatedPriceInfo?.showPercentage || site.priceInfo.showPercentage}
          discountPercentage={
            updatedPriceInfo?.discountPercentage || site.priceInfo.discountPercentage
          }
          highlighted={site.changed}
        />
      </CostContainer>
    </StyledBox>
  );
};
